import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { PROFILE } from "../lib/data";

const LINKS = [
  { label: "Practice", href: "#practice" },
  { label: "About", href: "#about" },
  { label: "Team", href: "#team" },
  { label: "Outcomes", href: "#outcomes" },
  { label: "Reviews", href: "#reviews" },
  { label: "Photos", href: "#photos" },
  { label: "Journal", href: "#journal" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[#262626]" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="font-serif text-2xl tracking-tight text-[#F7F5F2]">MS Bhatti</span>
          <span className="overline hidden sm:inline">& Associates</span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-[#8C8C8C] hover:text-[#F7F5F2] transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#C8A24B] after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={`tel:${PROFILE.phoneTel}`}
            data-testid="nav-call-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-sm bg-[#F7F5F2] text-[#0A0A0A] px-5 py-2.5 text-sm font-medium hover:bg-[#C8A24B] transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={1.75} /> Call Now
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            className="md:hidden text-[#F7F5F2]"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0A0A] border-b border-[#262626]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="font-serif text-2xl text-[#F7F5F2]"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
